/* MeetingDaySuggestionCard — kawaii pet-coached card that detects busy days
 * (3+ meetings or back-to-back) and offers to block focus time between them. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, X, Clock, Calendar, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISMISS_KEY = 'focus_suggestion_dismissed_v1';

function isDismissedToday(date) {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const map = JSON.parse(raw);
    return map[date] === new Date().toISOString().slice(0, 10);
  } catch { return false; }
}

function dismissForToday(date) {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[date] = new Date().toISOString().slice(0, 10);
    localStorage.setItem(DISMISS_KEY, JSON.stringify(map));
  } catch {/* ignore */}
}

function fmtTime(iso) {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default function MeetingDaySuggestionCard({ onPointsChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({}); // { date: Set(slotIndex) }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/focus-suggestions/upcoming`, { withCredentials: true });
      const filtered = (data?.suggestions || []).filter((s) => !isDismissedToday(s.date));
      setSuggestions(filtered);
      // Pre-select all slots
      const sel = {};
      filtered.forEach((s) => {
        sel[s.date] = new Set(s.available_focus_slots.map((_, i) => i));
      });
      setSelected(sel);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSlot = (date, idx) => {
    setSelected((prev) => {
      const cur = new Set(prev[date] || []);
      if (cur.has(idx)) cur.delete(idx); else cur.add(idx);
      return { ...prev, [date]: cur };
    });
  };

  const create = async (sug) => {
    const indices = Array.from(selected[sug.date] || []);
    if (!indices.length) { toast.error('Välj minst en lucka'); return; }
    const slots = indices.map((i) => ({
      start: sug.available_focus_slots[i].start,
      end: sug.available_focus_slots[i].end,
      title: '🧘 Fokustid',
    }));
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/focus-suggestions/create-focus-blocks`,
        { date: sug.date, slots }, { withCredentials: true });
      toast.success(`${data.created} fokusblock skapade 🌸`);
      dismissForToday(sug.date);
      load();
      onPointsChange?.();
    } catch { toast.error('Kunde inte skapa fokusblock'); }
    finally { setBusy(false); }
  };

  const dismiss = (sug) => {
    dismissForToday(sug.date);
    setSuggestions((prev) => prev.filter((s) => s.date !== sug.date));
  };

  if (loading) return null;
  if (suggestions.length === 0) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="meeting-day-suggestions">
      {suggestions.map((sug) => {
        const isExpanded = expanded[sug.date];
        const slotsSelected = selected[sug.date]?.size || 0;
        return (
          <div key={sug.date}
            className="neo-card p-3 bg-gradient-to-br from-[#FFF8FB] via-[#FFE8F5] to-[#E8F4FF]"
            data-testid={`suggestion-${sug.date}`}>
            <div className="flex items-start gap-2">
              <span className="text-3xl shrink-0">🐾</span>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase opacity-60 tracking-wide">Din pet säger</p>
                <p className="font-bold text-sm leading-tight">{sug.summary}</p>
                <p className="text-xs opacity-70 mt-1">
                  Vill jag blocka <b>{sug.available_focus_slots.length} fokus-luckor</b> mellan dina möten?
                </p>
              </div>
              <button onClick={() => dismiss(sug)} className="opacity-50 hover:opacity-100 p-1" data-testid={`suggestion-dismiss-${sug.date}`}>
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Slot preview */}
            {sug.available_focus_slots.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1" data-testid={`slots-${sug.date}`}>
                {sug.available_focus_slots.map((slot, i) => {
                  const picked = selected[sug.date]?.has(i);
                  return (
                    <button key={i}
                      onClick={() => toggleSlot(sug.date, i)}
                      className={`text-[11px] px-2 py-1 rounded-full border-2 font-bold transition ${picked ? 'bg-[#B9FBC0] border-[#4DAA4D]' : 'bg-white border-black/15 opacity-60'}`}
                      data-testid={`slot-${sug.date}-${i}`}>
                      {picked ? '✓ ' : ''}{fmtTime(slot.start)}–{fmtTime(slot.end)} ({slot.duration_min}m)
                    </button>
                  );
                })}
              </div>
            )}

            {/* Expand to see meetings */}
            <button
              onClick={() => setExpanded((e) => ({ ...e, [sug.date]: !e[sug.date] }))}
              className="text-[10px] opacity-60 hover:opacity-100 underline mt-2"
              data-testid={`suggestion-expand-${sug.date}`}
            >
              {isExpanded ? 'Dölj' : 'Visa'} möten ({sug.meeting_count})
            </button>

            {isExpanded && (
              <ul className="mt-2 space-y-1 text-xs neo-card p-2 bg-white/70" data-testid={`meetings-${sug.date}`}>
                {sug.meetings.map((m, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Clock className="w-3 h-3 opacity-50 shrink-0" />
                    <span className="font-bold opacity-80">{fmtTime(m.start)}–{fmtTime(m.end)}</span>
                    <span className="truncate">{m.title}</span>
                    {m.source === 'external' && (
                      <span className="text-[9px] bg-[#E8E0FF] px-1 rounded ml-auto shrink-0">🍎 {m.feed_name || 'extern'}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <div className="flex gap-2 mt-2">
              <button onClick={() => create(sug)} disabled={busy || slotsSelected === 0}
                className="neo-btn flex-1 py-2 bg-[#B9FBC0] text-xs font-bold flex items-center justify-center gap-1"
                data-testid={`suggestion-accept-${sug.date}`}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                  <><Sparkles className="w-3.5 h-3.5" /> Skapa {slotsSelected} fokusblock</>}
              </button>
              <button onClick={() => dismiss(sug)}
                className="neo-btn px-3 py-2 bg-white text-xs font-bold"
                data-testid={`suggestion-not-now-${sug.date}`}>
                Inte nu
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
